import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../contexts';
import { primary, white } from '../../style/colors';

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const BannerContainer = styled.div`
  background: ${primary[500]};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const NotificationItem = styled.div`
  max-width: 1920px;
  margin: 0 auto;
  padding: ${(props) => (props.collapsed ? '10px 40px' : '14px 40px')};
  display: flex;
  align-items: ${(props) => (props.collapsed ? 'center' : 'flex-start')};
  justify-content: space-between;
  gap: 16px;
  animation: ${slideDown} 0.3s ease-out;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: padding 0.2s ease;

  &:last-child {
    border-bottom: none;
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${white};
  border-radius: 50%;
  color: ${primary[500]};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  i {
    font-size: 16px;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h4`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${white};
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  display: flex;
  align-items: center;
  gap: 8px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  ${(props) =>
    props.clickable &&
    `
    &:hover {
      text-decoration: underline;
    }
  `}
`;

const ExpandIcon = styled.i`
  font-size: 18px;
  color: ${white};
  opacity: 0.8;
  transition: transform 0.2s ease;
`;

const Content = styled.p`
  margin: 4px 0 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  white-space: pre-line;
`;

const ActionButton = styled.button`
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${white};
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  i {
    font-size: 18px;
  }
`;

const NotificationBanner = () => {
  const { t } = useTranslation();
  const { notifications, dismissedIds, dismissNotification, expandNotification, loading } =
    useNotifications();

  if (loading || notifications.length === 0) {
    return null;
  }

  return (
    <BannerContainer>
      {notifications.map((notification) => {
        const isCollapsed = dismissedIds.includes(notification._id);

        return (
          <NotificationItem key={notification._id} collapsed={isCollapsed}>
            <IconWrapper>
              <i className="mdi mdi-bell" />
            </IconWrapper>

            <ContentWrapper>
              {isCollapsed ? (
                <Title
                  clickable
                  onClick={() => expandNotification(notification._id)}
                  title={t('expandNotification')}
                >
                  {notification.title}
                  <ExpandIcon className="mdi mdi-chevron-down" />
                </Title>
              ) : (
                <>
                  <Title>{notification.title}</Title>
                  <Content>{notification.content}</Content>
                </>
              )}
            </ContentWrapper>

            {!isCollapsed && (
              <ActionButton
                onClick={() => dismissNotification(notification._id)}
                title={t('dismissNotification')}
                aria-label={t('dismissNotification')}
              >
                <i className="mdi mdi-close" />
              </ActionButton>
            )}
          </NotificationItem>
        );
      })}
    </BannerContainer>
  );
};

export default NotificationBanner;
